import * as React from 'react';
import {cn} from '@purpose/ui/lib/utils';

function Skeleton({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      className={cn('bg-accent animate-pulse rounded-md', className)}
      {...props}
    />
  );
}

const Block = ({
  width,
  height,
  sx = {},
  className,
  withoutAnimation = false,
  ...props
}: {
  width?: string;
  height?: string;
  sx?: any;
  className?: string;
  withoutAnimation?: boolean;
} & React.ComponentProps<'div'>) => {
  const style = {
    width,
    height,
    ...sx
  };

  return (
    <div
      className={cn(
        'bg-accent rounded-md',
        withoutAnimation ? 'animate-none' : 'animate-pulse',
        className
      )}
      style={style}
      {...props}
    />
  );
};

const CollapsedGrid = ({
  sx = {},
  doubleRow = false,
  withButton = false,
  className,
  ...props
}: {
  sx?: any;
  doubleRow?: boolean;
  withButton?: boolean;
  className?: string;
} & React.ComponentProps<'div'>) => {
  const style = {
    ...sx
  };

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      style={style}
      {...props}
    >
      {Array.from({length: doubleRow ? 2 : 1}, (_, rowIndex) => (
        <div key={rowIndex} className="flex gap-2">
          {Array.from({length: 3}, (_, colIndex) => (
            <div
              key={colIndex}
              className="bg-accent h-16 flex-1 animate-pulse rounded-md"
            />
          ))}
        </div>
      ))}
      {withButton && (
        <div className="mt-2 flex justify-center">
          <div className="bg-accent h-8 w-32 animate-pulse rounded-md" />
        </div>
      )}
    </div>
  );
};

const CategotyButtonsGrid = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div className={cn('relative flex gap-2', className)} {...props}>
    {Array.from({length: 10}, (_, i) => i + 1).map(id => (
      <div
        key={id}
        className="bg-accent h-14 w-[217px] min-w-[217px] animate-pulse rounded-md"
      />
    ))}
  </div>
);

const Carousel = ({className, ...props}: React.ComponentProps<'div'>) => (
  <div className={cn('flex flex-col gap-2', className)} {...props}>
    <div className="bg-accent h-5 w-48 animate-pulse rounded-md" />
    <div className="flex gap-2">
      {Array.from({length: 3}, (_, i) => (
        <div key={i} className="bg-accent h-32 w-48 animate-pulse rounded-md" />
      ))}
    </div>
  </div>
);

const Table = ({
  className,
  isSquareImage = false,
  ...props
}: React.ComponentProps<'div'> & {isSquareImage?: boolean}) => (
  <div className={cn('flex flex-col gap-2 pl-16', className)} {...props}>
    <div className="flex flex-col gap-3">
      {Array.from({length: 10}, (_, i) => (
        <div key={i} className="flex items-center gap-10">
          <div
            className={cn(
              'bg-accent animate-pulse',
              isSquareImage
                ? 'h-18 w-26.25 rounded-md'
                : 'h-16 w-16 rounded-full'
            )}
          />
          <div
            className={cn(
              'bg-accent h-3 animate-pulse rounded-md',
              ['w-19.5', 'w-30', 'w-46.5'][i % 3]
            )}
          />
        </div>
      ))}
    </div>
  </div>
);

const List = ({className, ...props}: React.ComponentProps<'div'>) => (
  <div className={cn('flex flex-col pl-10', className)} {...props}>
    <div className="flex flex-col gap-3">
      {Array.from({length: 10}, (_, i) => (
        <div key={i} className="flex items-center gap-10">
          <div className="bg-accent h-3 w-3 animate-pulse rounded-full" />
          <div className="flex flex-col gap-3">
            <div className="bg-accent h-3.5 w-[221px] animate-pulse rounded-md" />
            <div className="bg-accent h-3 w-[911px] animate-pulse rounded-md" />
            <div className="bg-accent h-3 w-[640px] animate-pulse rounded-md" />
          </div>
          <div className="bg-accent h-3.5 w-18 animate-pulse rounded-md" />
        </div>
      ))}
    </div>
  </div>
);

const Stats = ({className, ...props}: React.ComponentProps<'div'>) => (
  <div className={cn('flex flex-col gap-3 pl-10', className)} {...props}>
    <div className="flex gap-3">
      {Array.from({length: 10}, (_, i) => (
        <Block key={`key_1_${i}`} className="min-h-[120px] min-w-[212px]" />
      ))}
    </div>
    <div className="flex gap-3">
      {Array.from({length: 5}, (_, i) => (
        <Block key={`key_2_${i}`} className="min-h-[389px] min-w-[751px]" />
      ))}
    </div>
    <div className="flex gap-3">
      {Array.from({length: 8}, (_, i) => (
        <Block key={`key_3_${i}`} className="min-h-[382px] min-w-[561px]" />
      ))}
    </div>
  </div>
);

// Attach sub-components to main Skeleton
Skeleton.Block = Block;
Skeleton.CollapsedGrid = CollapsedGrid;
Skeleton.CategotyButtonsGrid = CategotyButtonsGrid;
Skeleton.Carousel = Carousel;
Skeleton.Table = Table;
Skeleton.List = List;
Skeleton.Stats = Stats;

export {Skeleton};
